// First-party, cookieless analytics (Plausible-style). The browser sends only
// non-PII page context; the backend derives a daily-rotating, anonymous visitor
// hash from the request IP + user-agent and never stores either raw. Because no
// cookie/storage/persistent ID is set, this needs no consent banner.
const ANALYTICS_COLLECT_URL = 'https://hb-api-next-218506511331.us-east4.run.app/api/site/collect';
// const ANALYTICS_COLLECT_URL = 'http://localhost:3000/api/site/collect'; //dev

const PRODUCTION_HOSTNAME = 'objects.henrybuilt.com';

// Build the pageview payload and send it via sendBeacon (fetch keepalive
// fallback). Reads/writes no cookies, localStorage, or sessionStorage, and sends
// no IP or client-generated identifier.
const sendPageviewBeacon = (path) => {
  if (typeof window === 'undefined') return;

  const userIsEmployee = document.cookie
    .split('; ')
    .some((cookie) => cookie === 'userIsEmployee=true');
  if (userIsEmployee) return;

  // Skip automated/headless browsers (mirrors Plausible's client-side check).
  if (
    window._phantom ||
    window.__nightmare ||
    navigator.webdriver ||
    window.Cypress
  ) {
    return;
  }

  // Building the payload and sending are wrapped together: analytics must never
  // break the page, and some payload sources (URL, URLSearchParams, Intl) can
  // throw or be undefined on older browsers in our broad browserslist.
  try {
    // Canonical site identifier (strip leading "www.") so apex and www are the same.
    const site = window.location.hostname.replace(/^www\./, '');
    const env = site === PRODUCTION_HOSTNAME ? 'prod' : 'dev';

    // Strip the query string from the referrer to avoid capturing PII (e.g. emails
    // in tracking params).
    let referrerHost = '';
    let referrerPath = '';
    if (document.referrer) {
      try {
        const ref = new URL(document.referrer);
        referrerHost = ref.hostname;
        referrerPath = ref.pathname;
      }
      catch (e) {
        // ignore malformed referrer
      }
    }

    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['source', 'medium', 'campaign', 'term', 'content'].forEach((key) => {
      const value = params.get(`utm_${key}`);
      if (value) utm[key] = value;
    });

    const payload = {
      event: 'pageview',
      env,
      site,
      path,
      referrer_host: referrerHost,
      referrer_path: referrerPath,
      utm,
      screen_w: window.screen && window.screen.width,
      screen_h: window.screen && window.screen.height,
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      title: document.title
    };

    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      // text/plain is CORS-safelisted, so the cross-origin beacon avoids a
      // preflight (which the wildcard CORS config would block). The collect
      // endpoint parses the string body as JSON.
      const blob = new Blob([body], {type: 'text/plain'});
      navigator.sendBeacon(ANALYTICS_COLLECT_URL, blob);
    }
    else if (typeof fetch === 'function') {
      fetch(ANALYTICS_COLLECT_URL, {
        method: 'POST',
        keepalive: true,
        headers: {'Content-Type': 'application/json'},
        body
      });
    }
  }
  catch (e) {
    // analytics must never break the page
  }
};

export const trackPageView = (pathname) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'po_route_change',
    page_path: pathname,
    page_location: window.location.href,
    page_title: document.title
  });

  // First-party cookieless pageview tracking. Fires on initial load and on each
  // client-side route change (RouteTracker calls this on mount and navigation).
  sendPageviewBeacon(pathname);
};
